import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { fetchAuthSession } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { useLoading } from "../../context/LoadingContext";
import { useExportTableToXLSX } from "../../hooks/useExportTableToXLSX";
import { useClinicalEvaluationStore } from "../../store/useClinicalEvaluationStore";
import { getSelectedStudyProductName } from "../../utils/clinicalStudy";
import { rememberClinicalExportSheet } from "../../utils/clinicalEvalExportTables";
import { checkPipelineLoss, extractPmid, normalizePmids } from "../../utils/clinicalEvalPipeline";
import { buildWsUrl } from "../../utils/wsUrl";
import Table from "../Table";
import {
  claimsColumn,
  pmidColumn,
  remarksColumn,
  safetyColumn,
  searchTermColumn,
  subjectPopulationColumn,
  titleColumn,
} from "./Columns";
import { RegulifyService } from "../../api/RegulifyService";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Hazard Identification request. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_URL = (token) =>
  buildWsUrl("extract_subject_claims_safety", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor("title", titleColumn),
  columnHelper.accessor((row) => row.pmid, pmidColumn),
  columnHelper.accessor((row) => row.search_term, searchTermColumn),
  columnHelper.accessor(
    (row) => row.subject_population,
    subjectPopulationColumn,
  ),
  columnHelper.accessor((row) => row.claims, claimsColumn),
  columnHelper.accessor((row) => row.safety, safetyColumn),
  columnHelper.accessor((row) => row.relevancy?.reasoning, remarksColumn),
];

const Claims = ({ goToNextTab }) => {
  const { loading, setLoading} = useLoading();
  const { selectedStudy, selectedAppraisalTables, pipelineStageData, setPipelineStage } =
    useClinicalEvaluationStore(
      useShallow((state) => ({
        selectedStudy: state.selectedStudy,
        selectedAppraisalTables: state.selectedAppraisalTables,
        pipelineStageData: state.pipelineStageData,
        setPipelineStage: state.setPipelineStage,
      })),
    );
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([]);
  const exportTableToXLSX = useExportTableToXLSX();
  const studyName = getSelectedStudyProductName(selectedStudy);
  const f1Enabled = selectedAppraisalTables?.f1 === true;
  const f2Enabled = selectedAppraisalTables?.f2 === true;

  useEffect(() => {
    const fetchData = async (code) => {
      try {
        if (!studyName) {
          toast.error("studyName is missing!");
          return;
        }
        if (!f1Enabled && !f2Enabled) {
          toast.error("Select at least one grading table (F1 or F2).");
          return;
        }
        setLoading(true, "Extracting subject claims… This may take a few minutes. Thank you for waiting.", "text-green-600");
        let keepAliveInterval;
        
        await RegulifyService.get_allowed_credits(5);
        
        const token = await getAuthToken();
        const ws = new WebSocket(WS_URL(token));

        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          keepAliveInterval = startKeepAlive(ws);

          const payload = {
            product_name: studyName,
            f1_method: f1Enabled ? 1 : 0,
            f2_method: f2Enabled ? 1 : 0,
          };

          ws.send(JSON.stringify(payload));
          toast.info("Processing started...");
        };

        ws.onmessage = (event) => {
          const data = event.data;

          try {
            const parsed = JSON.parse(data);

            if (handleParsedMessage(parsed, { setLoading })) { return; }

            if (parsed?.all_extracted_data) {
              const currentPmids = normalizePmids(
                parsed.all_extracted_data.map((item) => extractPmid(item)),
              );
              const prevStage = pipelineStageData?.appraisal;
              if (prevStage?.studyName === studyName) {
                checkPipelineLoss({
                  previousStage: "Appraisal",
                  currentStage: "Claims",
                  previousPmids: prevStage.pmids,
                  currentPmids,
                });
              }
              setPipelineStage("claims", currentPmids, studyName);
              rememberClinicalExportSheet(studyName, "claims", {
                data: parsed.all_extracted_data,
                columns,
              });
              setData(parsed.all_extracted_data);
              return;
            } else {
              console.log("Unexpected output content received:", parsed);
            }
          } catch {
            console.log("Non-JSON message received:", data);
          }
        };

        ws.onerror = () => {
          console.error(WS_ERROR_MESSAGE);
          toast.error(WS_ERROR_MESSAGE);
          setLoading(false);
        };

        ws.onclose = () => {
          console.log("WebSocket closed");
          clearInterval(keepAliveInterval);
          setLoading(false);
        };
      } catch (error) {
        console.error(UNHANDELLED_ERROR_MESSAGE, error);
        setLoading(false);
      }
    };
    fetchData().then();
  }, []);

  const table = useReactTable({
    data: data || [], // fallback to empty array
    columns,
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      columnPinning: {
        left: ["title", "pmid", "searchTerm"],
        right: [],
      },
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="h-[calc(100vh-180px)] px-4">
        {data && data.length > 0 && (
          <>
            <div className="mt-4 flex w-full justify-end gap-2">
              <input
                value={table.getState().globalFilter}
                className="input input-bordered w-full"
                onChange={(e) => table.setGlobalFilter(String(e.target.value))}
                placeholder="Filter results by any field"
              />
              <button
                className="btn tooltip tooltip-left"
                data-tip="Download the below data in excel format"
                onClick={() => exportTableToXLSX(data, columns)}
              >
                export
              </button>
              <button
                className="btn btn-primary tooltip tooltip-left"
                data-tip="Click to proceed"
                onClick={goToNextTab}
                disabled={loading}
              >
                Next
              </button>
            </div>
            {data && data.length > 0 && (
              <div className="mt-4 max-h-[calc(100vh-280px)] overflow-x-auto overflow-y-auto">
                <Table table={table} className="text-black" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Claims;
